import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  AuthenticationService,
  LoginType,
  LoginTypeResponse,
} from '@core/service/authentication.service';
import { Router } from '@angular/router';
import { BaseComponent } from '@core/base.component';
import { VerificationTokenService } from '@core/service/verification-token.service';
import { Market } from '@core/dto/Market';
import { LocaleService } from '@core/service/locale.service';
import { EMAIL_PATTERN } from '@core/patterns';
import { LoggingService } from '@core/service/logging.service';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-email',
  templateUrl: './email.component.html',
  styleUrls: ['../login-pages.scss', './email.component.css'],
})
export class EmailComponent extends BaseComponent implements OnInit {
  form: FormGroup;
  formError = '';
  showError = false;

  loginMsg = '';
  processingLogin = false;

  EMAIL_KEY = 'email';

  emailInputPlaceholder = $localize`:@@login.email.input.email.placeholder:Enter business email`;
  emailInputErrorMessage = $localize`:@@login.email.input.email.errorMessage:Please enter a valid email address`;

  representativeMarkets: Market[];

  currentLocale: string;

  constructor(
    public localeService: LocaleService,
    private authenticationService: AuthenticationService,
    private router: Router,
    protected auth: AuthenticationService,
    private tokenService: VerificationTokenService,
    private loggingService: LoggingService
  ) {
    super(auth);
  }

  ngOnInit() {
    this.currentLocale = this.localeService.getCurrentLocale();

    let storedEmail = null;
    try {
      storedEmail = localStorage.getItem(this.EMAIL_KEY);
      if (storedEmail === 'undefined' || storedEmail === 'null') {
        storedEmail = null;
      }
    } catch (e) {}
    const shouldRememberMe = storedEmail !== '';

    this.form = new FormGroup({
      email: new FormControl(storedEmail ? storedEmail : '', [
        Validators.required,
        Validators.pattern(EMAIL_PATTERN),
      ]),
      rememberMe: new FormControl(shouldRememberMe),
    });
  }

  submit() {
    if (this.processingLogin) {
      return;
    }
    super.clearError();

    // trim whitespace from the email field
    const emailCtrl = this.form.get('email');
    if (emailCtrl && typeof emailCtrl!.value === 'string') {
      emailCtrl.setValue(emailCtrl.value.trim());
    }

    if (this.form.valid) {
      this.disableForm();
      this.formError = null;
      this.loginMsg = $localize`:@@login.email.loginMsg:Starting ...`;
      this.form.markAsUntouched();

      const email = this.form.value.email;
      const rememberMe = this.form.value.rememberMe;

      this.authenticationService
        .getLoginTypeForEmail(email)
        .then((loginTypeResponse: LoginTypeResponse) => {
          if (rememberMe && email) {
            localStorage.setItem(this.EMAIL_KEY, email);
          }
          this.representativeMarkets = loginTypeResponse.marketRepresentations;

          switch (loginTypeResponse.loginType) {
            case LoginType.BANKID:
              this.router.navigate(['/bankid-login'], {
                queryParams: { email: email },
              });
              break;
            case LoginType.NO_USER:
              this.router
                .navigate(['/email-not-found'], {
                  queryParams: { email: email },
                })
                .then(() => false);
              break;
            case LoginType.CREATE_USER:
              this.tokenService.email = email;
              this.router.navigate(['/signup-token'], {
                queryParams: { email: email },
              });
              // the domain name of the email-adress is white listed for account creation, go into account creation flow.
              // this is currently handled entirely in the back end, but that might change if we want to give better feedback.
              break;
            case LoginType.PENDING_ADMIN_APPROVAL:
              this.tokenService.email = email;
              this.router.navigate(['/pending-admin-approval'], {
                queryParams: { email: encodeURIComponent(email) },
              });
              break;
            case LoginType.PENDING_USER_APPROVAL:
              this.tokenService.email = email;
              this.router.navigate(['/pending-user-approval'], {
                queryParams: { email: encodeURIComponent(email) },
              });
              break;
            case LoginType.PENDING_IDENTIFICATION:
              this.tokenService.email = email;
              this.router.navigate(['/identify-signatory-token'], {
                queryParams: { email: encodeURIComponent(email) },
              });
              break;
            case LoginType.VERIFY_EMAIL_ACCESS:
              this.tokenService.email = email;
              this.router.navigate(['/email-renew'], {
                queryParams: { email: encodeURIComponent(email) },
              });
              break;
            case LoginType.SET_PASSWORD:
              this.tokenService.email = email;
              this.router.navigate(['/token-entry'], {
                queryParams: { email: encodeURIComponent(email) },
              });
              break;
            case LoginType.PASSWORD:
            case LoginType.TWO_FACTOR:
              this.router.navigate(['/password-entry'], {
                queryParams: { email: encodeURIComponent(email) },
              });
              break;
          }
        })
        .catch((error) => {
          this.enableForm();
          super.handleError(error);
          this.loggingService.reportCrash(
            'LoginType not found: ' + error,
            null
          );
        });
    } else {
      this.showError = true;
      this.enableForm();
    }
  }

  setFormError(error: string) {
    this.formError = error;
    this.form.get('email').setErrors({ key: error });
  }

  private enableForm() {
    this.form.enable();
    this.processingLogin = false;
  }

  private disableForm() {
    this.processingLogin = true;
    this.form.disable();
  }

  rememberMeClicked(): void {
    if (!this.form.value.rememberMe) {
      // if the user un-checks the remember-me-checkbox we will set the value to '' (in contrast to just undefined);
      // this indicates that the user does not want to be remembered
      localStorage.setItem(this.EMAIL_KEY, '');
    }
  }

  isNotProduction(): boolean {
    return !environment.production;
  }
}
